import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, CircularProgress, Paper, Grid, Tooltip, Chip, IconButton, List, ListItem, ListItemButton, ListItemText, ListItemSecondaryAction, Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AlertTitle,
  Link,
  Button
} from "@mui/material";
import { ContentCopy as ContentCopyIcon, ExpandMore as ExpandMoreIcon, Help as HelpIcon, Info as InfoIcon } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Client } from "@microsoft/microsoft-graph-client";

interface ExtensionProperty {
  name: string;
  type: string;
  isMultiValued?: boolean;
}
interface DirectoryExtension {
  id: string;
  name?: string;
  targetObjects?: string[];
  properties?: ExtensionProperty[];
}
interface ApplicationInfo {
  id: string;
  appId: string;
  displayName: string;
}
interface ADDirectoryExtensionsManagerProps {
  getGraphClient: () => Promise<Client>;
  copyToClipboard: (text: string) => void;
  setErrorMessage: (msg: string) => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
  setSnackbarMessage: (msg: string) => void;
  setSnackbarSeverity: (s: "success" | "info" | "warning" | "error") => void;
  setSnackbarOpen: (open: boolean) => void;
}

const ADDirectoryExtensionsManager: React.FC<ADDirectoryExtensionsManagerProps> = ({
  getGraphClient,
  copyToClipboard,
  setErrorMessage,
  setLoading,
  loading,
  setSnackbarMessage,
  setSnackbarSeverity,
  setSnackbarOpen,
}) => {
  const [tenantSchemaApp, setTenantSchemaApp] = useState<ApplicationInfo | null>(null);
  const [directoryExtensions, setDirectoryExtensions] = useState<DirectoryExtension[]>([]);
  const [selectedDirectoryExt, setSelectedDirectoryExt] = useState<DirectoryExtension | null>(null);

  // Discover the Tenant Schema Extension App
  const discoverTenantSchemaExtensionApp = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const client = await getGraphClient();
      // Try to find the app by name pattern
      let appsResponse = await client.api('/applications')
        .filter("startswith(displayName, 'Tenant Schema Extension')")
        .select('id,appId,displayName')
        .top(10)
        .get();
      if (!appsResponse.value || appsResponse.value.length === 0) {
        // Try broader search
        appsResponse = await client.api('/applications')
          .filter("startswith(displayName, 'Tenant') or startswith(displayName, 'AAD Schema') or startswith(displayName, 'Azure AD Schema')")
          .select('id,appId,displayName')
          .top(20)
          .get();
      }
      // Fallback: client-side filter
      const potentialApps = appsResponse.value.filter((app: any) => {
        const name = app.displayName.toLowerCase();
        return name.includes('tenant') || name.includes('schema') || name.includes('extension') || name.includes('aad') || name.includes('entra');
      });
      if (potentialApps.length > 0) {
        // Pick the first app with extension properties
        for (const app of potentialApps) {
          const extResponse = await client.api(`/applications/${app.id}/extensionProperties`).get();
          if (extResponse.value && extResponse.value.length > 0) {
            setTenantSchemaApp(app);
            setDirectoryExtensions(extResponse.value.map((prop: any) => ({
              id: prop.id,
              name: prop.name,
              targetObjects: prop.targetObjects || [],
              properties: [{ name: prop.name, type: prop.dataType }]
            })));
            setSnackbarMessage(`Found ${extResponse.value.length} extensions in ${app.displayName}`);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            setSelectedDirectoryExt(null);
            setLoading(false);
            return;
          }
        }
        setSnackbarMessage("Found potential Tenant Schema Extension Apps but they contain no extensions");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage("Could not find Tenant Schema Extension App in your tenant");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setErrorMessage(`Failed to discover Tenant Schema Extension App: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }, [getGraphClient, setErrorMessage, setLoading, setSnackbarMessage, setSnackbarOpen, setSnackbarSeverity]);

  useEffect(() => {
    discoverTenantSchemaExtensionApp();
  }, [discoverTenantSchemaExtensionApp]);

  return (
    <Box>
      {/* Header Paper */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: alpha('#f5f9ff', 0.7) }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          AD  Directory Extensions
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            These extensions are synchronized from on-premises AD via Entra Connect and are managed by the <strong>Tenant Schema Extension App</strong>. Creation/deletion is not available here.
            <br /><br />
            When you configure directory extensions in Entra Connect, the system automatically:
          <ol>
            <li><Typography variant="body2" paragraph>Registers all your directory extensions under this application</Typography></li>
            <li><Typography variant="body2" paragraph>Assigns a unique prefix (Tenant Schema Extension App's AppId) that forms part of the extension name</Typography></li>
          </ol>
          </Typography>
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {tenantSchemaApp ? `App: ${tenantSchemaApp.displayName}` : "Searching for Tenant Schema Extension App..."}
          </Typography> */}
          {/* Removed the manual 'Find' button as discovery happens automatically */}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : directoryExtensions.length > 0 ? (
        <Grid container spacing={3}>
          {/* Left Panel */}
            <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: alpha('#f4f8fb', 0.7), flexShrink: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Extensions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {directoryExtensions.length} found
              </Typography>
              </Box>
              <CardContent sx={{ p: 0, flexGrow: 1, overflowY: 'auto' }}>
              <List sx={{ py: 0 }}>
                {directoryExtensions.map(ext => (
                <ListItem
                  key={ext.id}
                  disablePadding
                  sx={{ borderLeft: selectedDirectoryExt?.id === ext.id ? 3 : 0, borderColor: 'primary.main' }}
                >
                  <ListItemButton
                  selected={selectedDirectoryExt?.id === ext.id}
                  onClick={() => setSelectedDirectoryExt(ext)}
                  sx={{ '&:hover': { bgcolor: alpha('#000', 0.04) } }}
                  >
                  <ListItemText
                    primary={
                    <Tooltip title={ext.name || ext.id} placement="top-start">
                      <Typography 
                      noWrap 
                      sx={{ 
                        fontWeight: 500,
                        maxWidth: 'calc(100% - 40px)', // Leave space for the copy icon
                        display: 'block'
                      }}
                      >
                      {ext.name || ext.id.split('_').pop() || ext.id}
                      </Typography>
                    </Tooltip>
                    }
                    secondary={
                    <Tooltip title={ext.id} placement="bottom-start">
                      <Typography 
                      noWrap 
                      variant="caption" 
                      sx={{ 
                        display: 'block',
                        maxWidth: 'calc(100% - 40px)', // Leave space for the copy icon
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                        fontSize: '0.7rem'
                      }}
                      >
                      {ext.id}
                      </Typography>
                    </Tooltip>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Copy ID">
                    <IconButton 
                      edge="end" 
                      size="small" 
                      onClick={e => { e.stopPropagation(); copyToClipboard(ext.id); }}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                  </ListItemButton>
                </ListItem>
                ))}
              </List>
              </CardContent>
            </Card>
            </Grid>
          {/* Right Panel */}
          <Grid item xs={12} md={8}>
            {selectedDirectoryExt ? (
              <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: alpha('#f5f9ff', 0.7), flexShrink: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Details</Typography>
                </Box>
                <CardContent sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Basic Information</Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={3}><Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>ID:</Typography></Grid>
                      <Grid item xs={12} sm={9} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', flexGrow: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>{selectedDirectoryExt.id}</Typography>
                        <Tooltip title="Copy ID"><IconButton size="small" onClick={() => copyToClipboard(selectedDirectoryExt.id)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>
                      </Grid>
                      <Grid item xs={12} sm={3}><Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Name:</Typography></Grid>
                      <Grid item xs={12} sm={9} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', flexGrow: 1 }}>{selectedDirectoryExt.name || "N/A"}</Typography>
                        {selectedDirectoryExt.name && <Tooltip title="Copy Name"><IconButton size="small" onClick={() => copyToClipboard(selectedDirectoryExt.name!)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>}
                      </Grid>
                      <Grid item xs={12} sm={3}><Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Targets:</Typography></Grid>
                      <Grid item xs={12} sm={9}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selectedDirectoryExt.targetObjects?.length ? selectedDirectoryExt.targetObjects.map(target => <Chip key={target} label={target} size="small" />) : <Typography variant="body2" color="text.secondary">N/A</Typography>}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}><Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Data Type:</Typography></Grid>
                      <Grid item xs={12} sm={9}>
                        <Typography variant="body2">{selectedDirectoryExt.properties?.[0]?.type || 'Unknown'}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Claims Mapping Format</Typography>
                    <Box sx={{ bgcolor: alpha('#000', 0.03), p: 2, borderRadius: 1, overflow: 'auto', mb: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {`{\n  "Source": "User",\n  "ID": "${selectedDirectoryExt.name ? selectedDirectoryExt.name.split('_').pop() || selectedDirectoryExt.name : 'PropertyName'}",\n  "JwtClaimType": "'PropertyName'"\n}`}
                    </Typography>

                    </Box>
                    <Button variant="outlined" startIcon={<ContentCopyIcon />} sx={{ borderRadius: 2 }} size="small"
                      onClick={() => copyToClipboard(`{\n  "Source": "User",\n  "ID": "${selectedDirectoryExt.name || 'PropertyName'}",\n  "JwtClaimType": "'PropertyName'"\n}`)}>
                      Copy Format
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      Note: Replace `PropertyName` with the real claim name. This format is typical for JWT tokens.
                    </Typography>
                  </Paper>
                </CardContent>
              </Paper>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="text.secondary">Select an extension from the list to view details.</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
          <Typography color="text.secondary">No directory extensions found for the Tenant Schema Extension App.</Typography>
        </Box>
      )}

      {/* --- START: Added Informational Sections --- */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Common Directory Extensions
        </Typography>
        <Typography variant="body2" paragraph>
          The following are commonly used AD attributes that can be synchronized using Entra Connect's Directory Extensions feature:
        </Typography>
        <Grid container spacing={2}>
          {[
            'thumbnailPhoto', 'homePhone', 'division',
            'otherTelephone', 'employeeID', 'manager', 'assistant',
            'physicalDeliveryOfficeName', 'departmentNumber', 'employeeType',
            'employeeNumber', 'personalTitle'
          ].map((attr) => (
            <Grid item xs={6} sm={4} md={3} key={attr}>
              <Paper
                sx={{
                  p: 1.5, textAlign: 'center', borderRadius: 2,
                  backgroundColor: alpha('#4caf50', 0.07), border: '1px solid',
                  borderColor: alpha('#4caf50', 0.2), height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <Tooltip title={`AD attribute: ${attr}`}>
                  <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {attr}
                  </Typography>
                </Tooltip>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" sx={{ mt: 3 }}>
          This is just a subset of available attributes. See the
          <Link href="https://learn.microsoft.com/en-us/entra/identity/hybrid/connect/how-to-connect-sync-feature-directory-extensions"
            target="_blank" rel="noopener noreferrer" sx={{ ml: 0.5, mr: 0.5 }}>
            AD Directory Extensions documentation
          </Link>
          for the complete list of available attributes.
        </Typography>
      </Paper>

      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <HelpIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            How to Configure AD Directory Extensions
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>To configure directory extensions in Entra Connect:</Typography>
          <ol>
            <li><Typography variant="body2" paragraph>Open the Entra Connect configuration wizard</Typography></li>
            <li><Typography variant="body2" paragraph>Select "Customize synchronization options" and proceed to the "Directory Extension" page</Typography></li>
            <li><Typography variant="body2" paragraph>Select the AD attributes you want to synchronize to Entra ID</Typography></li>
            <li><Typography variant="body2" paragraph>Complete the configuration wizard</Typography></li>
          </ol>
          <Typography variant="body2" paragraph>After configuration, these attributes will be available in Entra ID with the following naming convention:</Typography>
          <Box sx={{ backgroundColor: alpha('#000', 0.03), p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
            extension_[prefix]_[attributeName]
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>Where [prefix] is specific to your Entra Connect installation.</Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Using AD Directory Extensions in Claims Mapping
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>Not all synchronized attributes can be used in claims mapping policies. There are important limitations:</Typography>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Important Limitation</AlertTitle>
            <Typography variant="body2">
              Only properties that are already part of the Entra ID schema (such as extensionAttribute1-15, employeeId, etc.)
              can be successfully used as custom claims in tokens. Other AD attributes synchronized through Entra Connect
              may appear in the user object but cannot be included in tokens.
            </Typography>
          </Alert>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Attributes that work in claims mapping:</Typography>
          <ul>
            <li>extensionAttribute1-15 (built-in extension attributes)</li>
            <li>employeeId (part of the base Entra ID schema)</li>
            <li>Other attributes that are already part of the Entra ID schema</li>
          </ul>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>Example of working claims mapping:</Typography>
          <Box sx={{ backgroundColor: alpha('#000', 0.03), p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem', mb: 2 }}>
            <pre style={{ margin: 0, overflowX: 'auto' }}>
              {`{\n  "Source": "user",\n  "ID": "employeeId",\n  "JwtClaimType": "employee_id"\n}`}
            </pre>
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>For Entra Connect synchronized directory extensions:</Typography>
          <Typography variant="body2" paragraph>When using Entra Connect to synchronize non-schema attributes (like employeeNumber, division, etc.), these attributes:</Typography>
          <ul>
            <li><strong>Can</strong> be accessed via Microsoft Graph API</li>
            <li><strong>Can</strong> be used for conditional access rules</li>
            <li><strong>Cannot</strong> be reliably included in tokens via claims mapping policies</li>
          </ul>
          <Box sx={{ backgroundColor: alpha('#f5f9ff', 0.7), p: 2, borderRadius: 1, border: '1px solid', borderColor: 'primary.light', mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Recommended Approach:</Typography>
            <Typography variant="body2">
              If you need custom AD attributes in tokens, map them to one of the 15 extension attributes
              in your AD Connect synchronization rules. These extension attributes can then be reliably
              used in claims mapping policies.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
      {/* --- END: Added Informational Sections --- */}


    </Box>
  );
};

export default ADDirectoryExtensionsManager;